/**
 * OTA照合 (/ota-check) の CSV 抽出ロジック
 *
 * React に依存しない純関数だけを置く（テスト対象）。UI は ../OtaCheck.tsx。
 * 2026-08-16: OKIREN / レンナビ 追加時に OtaCheck.tsx から切り出し
 */

/** OTA横断の正規化済み予約行 */
export interface OtaRow {
    reservationNo: string;
    status: string;
    /** false = A/B 判定の対象 (有効な予約)。true = キャンセル等で対象外 */
    isCancelled: boolean;
    startDate: string;
    endDate: string;
    customerName: string;
    driverName: string;
    driverPhone: string;
    totalPrice: string;
    applyDate: string;
    cancelDate: string;
    plan: string;
    vehicleClass: string;
}

// --- CSV parser (quoted fields + multi-line対応) -------------------------------
export function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let cur: string[] = [];
    let field = '';
    let inQuotes = false;
    let i = 0;
    while (i < text.length) {
        const ch = text[i];
        if (inQuotes) {
            if (ch === '"') {
                if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
                inQuotes = false; i++; continue;
            }
            field += ch; i++;
        } else {
            if (ch === '"') { inQuotes = true; i++; continue; }
            if (ch === ',') { cur.push(field); field = ''; i++; continue; }
            if (ch === '\r' || ch === '\n') {
                cur.push(field); field = '';
                if (!(cur.length === 1 && cur[0] === '')) rows.push(cur);
                cur = [];
                if (ch === '\r' && text[i + 1] === '\n') i += 2; else i++;
                continue;
            }
            field += ch; i++;
        }
    }
    if (field !== '' || cur.length > 0) { cur.push(field); if (!(cur.length === 1 && cur[0] === '')) rows.push(cur); }
    return rows;
}

// --- Shift-JIS 優先でデコード（UTF-8 フォールバック） -------------------------
export async function decodeFile(file: File): Promise<string> {
    const buf = await file.arrayBuffer();
    // まず shift_jis
    try {
        const sjis = new TextDecoder('shift-jis', { fatal: false }).decode(buf);
        if (sjis.includes('予約番号') || sjis.includes('事業者名') || sjis.includes('ステータス')) return sjis;
    } catch (_) { /* noop */ }
    // fallback UTF-8（BOM は除去）
    const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(buf);
    return utf8.charCodeAt(0) === 0xfeff ? utf8.slice(1) : utf8;
}

/** ヘッダ行から列index を引く小道具 */
function headerIndexer(header: string[]) {
    const trimmed = header.map(c => c.replace(/^﻿/, '').trim());
    return (name: string) => trimmed.findIndex(h => h === name);
}

const cell = (row: string[], i: number): string => (i < 0 ? '' : (row[i] || '').trim());

/** 「2026/08/04」+「12:30」→「2026/08/04 12:30」 */
const joinDateTime = (d: string, t: string): string => (d && t ? `${d} ${t}` : d || '');

// --- じゃらんCSV → 予約行 抽出 ------------------------------------------------
export function extractJalanRows(text: string): OtaRow[] {
    const rows = parseCsv(text);
    if (rows.length < 2) return [];
    const idx = headerIndexer(rows[0]);
    const iRes = idx('予約番号');
    const iStatus = idx('ステータス');
    const iStart = idx('貸出日時');
    const iEnd = idx('返却日時');
    const iName = idx('予約者氏名');
    const iDriver = idx('運転者氏名');
    const iPhone = idx('運転者電話番号');
    const iTotal = idx('合計金額（予約時）');
    const iApply = idx('申込日');
    const iCancel = idx('キャンセル日');
    const iPlan = idx('料金プラン');
    const iClass = idx('車両クラス');

    if (iRes < 0 || iStatus < 0) {
        throw new Error('必須列 (予約番号 / ステータス) がCSVヘッダに見つかりません');
    }

    const out: OtaRow[] = [];
    for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const resNo = cell(row, iRes);
        if (!resNo) continue;
        const status = cell(row, iStatus);
        out.push({
            reservationNo: resNo,
            status,
            // 「予約成立」以外はA/B判定の対象外にする
            //   キャンセル（予約者都合） / キャンセル（事業者都合） / 来店なし などを全て除外
            isCancelled: status !== '予約成立',
            startDate: cell(row, iStart),
            endDate: cell(row, iEnd),
            customerName: cell(row, iName),
            driverName: cell(row, iDriver),
            driverPhone: cell(row, iPhone),
            totalPrice: cell(row, iTotal),
            applyDate: cell(row, iApply),
            cancelDate: cell(row, iCancel),
            plan: cell(row, iPlan),
            vehicleClass: cell(row, iClass),
        });
    }
    return out;
}

// --- OKIREN CSV → 予約行 抽出 -------------------------------------------------
// ヘッダ例:
//   予約番号,予約受付日,車両タイプ,車両,プラン名,料金,担当者,ステータス,貸渡日,貸渡時間,返却日,返却時間
// ステータス: 予約確定 / 返却済 / 予約キャンセル
export function extractOkirenRows(text: string): OtaRow[] {
    const rows = parseCsv(text);
    if (rows.length < 2) return [];
    const idx = headerIndexer(rows[0]);
    const iRes = idx('予約番号');
    const iStatus = idx('ステータス');
    const iStartD = idx('貸渡日');
    const iStartT = idx('貸渡時間');
    const iEndD = idx('返却日');
    const iEndT = idx('返却時間');
    const iTotal = idx('料金');
    const iApply = idx('予約受付日');
    const iPlan = idx('プラン名');
    const iClass = idx('車両タイプ');
    const iCar = idx('車両');

    if (iRes < 0 || iStatus < 0) {
        throw new Error('必須列 (予約番号 / ステータス) がCSVヘッダに見つかりません');
    }

    const out: OtaRow[] = [];
    for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const resNo = cell(row, iRes);
        if (!resNo) continue;
        const status = cell(row, iStatus);
        const carClass = cell(row, iClass);
        const car = cell(row, iCar);
        out.push({
            reservationNo: resNo,
            status,
            // OKIREN は「予約キャンセル」のみ無効。予約確定 / 返却済 は有効扱い
            isCancelled: status.includes('キャンセル'),
            startDate: joinDateTime(cell(row, iStartD), cell(row, iStartT)),
            endDate: joinDateTime(cell(row, iEndD), cell(row, iEndT)),
            // OKIREN CSV には氏名列が無い
            customerName: '',
            driverName: '',
            driverPhone: '',
            totalPrice: cell(row, iTotal),
            applyDate: cell(row, iApply),
            cancelDate: '',
            plan: cell(row, iPlan),
            vehicleClass: car ? `${carClass} / ${car}` : carClass,
        });
    }
    return out;
}

// --- レンナビ CSV → 予約行 抽出 -----------------------------------------------
// ヘッダ例（Shift-JIS・列数が多い）:
//   予約番号,予約ステータス,…,料金プラン名称,貸出日時,…,返却日時,…,キャンセル日時,…,
//   車両クラス,…,姓(申込者),名(申込者),…,電話番号(申込者),…,予約合計料金(税込),…,申込日時
// 予約ステータス: 予約 / キャンセル
export function extractRennaviRows(text: string): OtaRow[] {
    const rows = parseCsv(text);
    if (rows.length < 2) return [];
    const idx = headerIndexer(rows[0]);
    const iRes = idx('予約番号');
    const iStatus = idx('予約ステータス');
    const iStart = idx('貸出日時');
    const iEnd = idx('返却日時');
    const iLast = idx('姓(申込者)');
    const iFirst = idx('名(申込者)');
    const iKanaUser = idx('フリガナ(利用者)');
    const iPhone = idx('電話番号(申込者)');
    const iTotal = idx('予約合計料金(税込)');
    const iApply = idx('申込日時');
    const iCancel = idx('キャンセル日時');
    const iPlan = idx('料金プラン名称');
    const iClass = idx('車両クラス');

    if (iRes < 0 || iStatus < 0) {
        throw new Error('必須列 (予約番号 / 予約ステータス) がCSVヘッダに見つかりません');
    }

    const out: OtaRow[] = [];
    for (let r = 1; r < rows.length; r++) {
        const row = rows[r];
        const resNo = cell(row, iRes);
        if (!resNo) continue;
        const status = cell(row, iStatus);
        const name = [cell(row, iLast), cell(row, iFirst)].filter(Boolean).join(' ');
        out.push({
            reservationNo: resNo,
            status,
            // レンナビは「キャンセル」のみ無効。「予約」は有効扱い
            isCancelled: status.includes('キャンセル'),
            startDate: cell(row, iStart),
            endDate: cell(row, iEnd),
            customerName: name,
            driverName: cell(row, iKanaUser),
            // レンナビは電話番号を '09012345678' のようにクォートで囲って出力する
            driverPhone: cell(row, iPhone).replace(/^'|'$/g, ''),
            totalPrice: cell(row, iTotal),
            applyDate: cell(row, iApply),
            cancelDate: cell(row, iCancel),
            plan: cell(row, iPlan),
            vehicleClass: cell(row, iClass),
        });
    }
    return out;
}

// --- システム側ステータスがキャンセル扱いか -----------------------------------
const CANCEL_LIKE = new Set(['cancelled', 'nonreserved']);
export const isSystemCancelled = (r: any): boolean => {
    const s = String(r.status || r.status_code || '').toLowerCase();
    if (CANCEL_LIKE.has(s)) return true;
    const n = Number(r.status);
    return n === 4 || n === 5;
};
